import React, { useRef, useState, useEffect } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ExclamationCircleFill, ThreeDots } from 'react-bootstrap-icons';
import { formatMessage } from '../../../translations/format-message';
import img404 from '../../../assets/Images/404.jpg';
import { FormattedMessage } from 'react-intl';
import TeplarPrintTable from '../components/teplar-table';
import BOMTable from '../components/bom-table';
import CheckItemsData from '../components/check-item-data';

interface OperatorHorizontalViewProps {
  operatorTeplarDetails: any[];
  mergedTabs: any[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  setMemo: React.Dispatch<React.SetStateAction<string[]>>;
  setSubmittedCheckItems: React.Dispatch<React.SetStateAction<any>>;
  dimensionTable: any[];
  dims: any[];
  dimensionChecks: { [key: string]: boolean };
  handleDimensionCheck: (
    tabIndex: number,
    rowIndex: number,
    dimId: number,
    checked: boolean
  ) => void;
  isTimerRunning: boolean;
  productImages: any[];
  processType: number | string;
  isProcessStarted: boolean;
  handleDecrementPrintCount: (index: number) => void;
  printCount: number[];
  handleChangePrintCount: (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => void;
  handleIncrementPrintCount: (index: number) => void;
  stringToHex: (text: string) => string;
  onSaveTeplarOrder: (
    orderData: { Id: number; Order: number; IsPrinted: boolean }[]
  ) => void;
  onTeplarOrderChange?: (
    orderData: { Id: number; Order: number; IsPrinted: boolean }[]
  ) => void;
  checkItems: any[];
  submittedCheckItems: any;
  handleCheckboxChange: (
    tabIndex: number,
    itemId: number,
    checked: boolean
  ) => void;
  handleMemoChange: (tabIndex: number, value: string) => void;
  memo: Record<number, string>;
  handleInfoChange: (tabIndex: number, itemId: number, value: string) => void;
  validFiles: any[];
  allFilesAreNull: boolean;
  canvasDrawings: Record<string, string>;
  handleCanvasChange: (key: string, dataUrl: string) => void;
  // bomDataList: any[];
  bomDataListState: any[];
  setBomDataListState: React.Dispatch<React.SetStateAction<any[]>>;
  tabValidationMap: Record<number, boolean>;
  showValidationWarnings: boolean;
  bomDataList: any[];
  teplarList: any[];
  setTeplarList: React.Dispatch<React.SetStateAction<any[]>>;
  saveClicked: boolean;
  setSaveClicked: React.Dispatch<React.SetStateAction<boolean>>;
}

const OperatorHorizontalView: React.FC<OperatorHorizontalViewProps> = ({
  operatorTeplarDetails,
  mergedTabs,
  activeIndex,
  setActiveIndex,
  setMemo,
  setSubmittedCheckItems,
  dimensionTable,
  dims,
  dimensionChecks,
  handleDimensionCheck,
  isTimerRunning,
  productImages,
  processType,
  isProcessStarted,
  handleDecrementPrintCount,
  printCount,
  handleChangePrintCount,
  handleIncrementPrintCount,
  stringToHex,
  onSaveTeplarOrder,
  onTeplarOrderChange,
  checkItems,
  submittedCheckItems,
  handleCheckboxChange,
  handleMemoChange,
  memo,
  handleInfoChange,
  validFiles,
  allFilesAreNull,
  canvasDrawings,
  handleCanvasChange,
  bomDataListState,
  setBomDataListState,
  tabValidationMap,
  showValidationWarnings,
  bomDataList,
  teplarList,
  setTeplarList,
  saveClicked,
  setSaveClicked,
}) => {
  const openFilesInNewWindow = (fileDetails: any[] = []) => {
    const files = fileDetails.filter((file: any) => file?.S3FilePath);
    if (files.length === 0) return;

    const newWindow = window.open('', '_blank');
    if (!newWindow) return;

    const fileMarkup = files
      .map((file: any) => {
        if (file.FileType === 'application/pdf') {
          return `<iframe src="${file.S3FilePath}#view=FitH&zoom=page-width" style="width:100%;min-height:100vh;border:none;margin-bottom:0px;"></iframe>`;
        }
        return `<img src="${file.S3FilePath}" style="width:100%;margin-bottom:12px;" />`;
      })
      .join('');

    newWindow.document.write(`
      <html>
        <body style="margin:0">
          ${fileMarkup}
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const ulRef = useRef<HTMLUListElement>(null);
  const tabRefs = useRef<(HTMLLIElement | null)[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showTabDropdown, setShowTabDropdown] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (ulRef.current) {
        setIsOverflowing(ulRef.current.scrollWidth > ulRef.current.clientWidth);
      }
    };
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    if (ulRef.current) observer.observe(ulRef.current);
    return () => observer.disconnect();
  }, [mergedTabs]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowTabDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollTabIntoViewIfNeeded = (originalIndex: number) => {
    const tabEl = tabRefs.current[originalIndex];
    const ulEl = ulRef.current;
    if (!tabEl || !ulEl) return;

    const tabRect = tabEl.getBoundingClientRect();
    const ulRect = ulEl.getBoundingClientRect();
    const isFullyVisible =
      tabRect.left >= ulRect.left && tabRect.right <= ulRect.right;

    if (!isFullyVisible) {
      tabEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  const handleDropdownTabClick = (originalIndex: number) => {
    setActiveIndex(originalIndex);
    setShowTabDropdown(false);
    scrollTabIntoViewIfNeeded(originalIndex);
  };

  const sortedTabs = mergedTabs
    .map((item, index) => ({ item, originalIndex: index }))
    .sort((a, b) => {
      const isTrue = (val: any) =>
        val === true || String(val).toLowerCase() === 'true';
      const getPriority = (item: any) => {
        if (isTrue(item.data?.IsDefault) || isTrue(item.data?.isDefault))
          return 0;
        if (isTrue(item.data?.IsDimension) || isTrue(item.data?.isDimension))
          return 1;
        return 2;
      };
      return getPriority(a.item) - getPriority(b.item);
    });

  return (
    <div className="layout-container horizontal">
      <div className="row">
        {/* Box 1: Tabs and Images/Dimension Table */}
        <div
          className={`${Number(processType) === 2 ? 'col-8' : `${Number(processType) === 3 ? 'col-4 col-xxl-4 picking-horizontal' : 'col-8 print-horizontal-dimension'}`}`}
        >
          <div className="operator-tabs-dimesnion position-relative">
            <ul
              ref={ulRef}
              className={`nav nav-tabs ${isOverflowing ? 'overflowing' : ''}`}
              id="imageDataTypeTab"
              role="tablist"
            >
              {sortedTabs.map(({ item: tabItem, originalIndex }) => {
                const hasTabWarning =
                  showValidationWarnings &&
                  tabValidationMap[originalIndex] === false;

                return (
                  <OverlayTrigger
                    key={originalIndex}
                    placement="top"
                    container={ulRef}
                    overlay={
                      <Tooltip id={`operator-tab-title-${originalIndex}`}>
                        {tabItem.Heading}
                      </Tooltip>
                    }
                  >
                    <li
                      ref={(el) => {
                        tabRefs.current[originalIndex] = el;
                      }}
                      className={`textTabBlock${activeIndex === originalIndex ? 'active textTabBlockactive' : ''}`}
                      onClick={() => {
                        setActiveIndex(originalIndex);
                        const currentFileItem = tabItem.data;
                        const memoKey =
                          currentFileItem?.TabIndex ?? originalIndex;

                        setMemo((prev) => {
                          if (prev[memoKey] !== undefined) return prev;
                          return { ...prev, [memoKey]: '' };
                        });

                        setSubmittedCheckItems((prev: any) => {
                          if (tabItem.data?.CheckItems) {
                            const hasAnyCheckItem =
                              tabItem.data.CheckItems.some(
                                (item: any) => prev[item.Id] !== undefined
                              );
                            if (hasAnyCheckItem) return prev;
                          }
                          return prev;
                        });

                        scrollTabIntoViewIfNeeded(originalIndex);
                      }}
                      // onDoubleClick={() => {
                      //   const files = tabItem.data?.FileDetail || [];
                      //   if (files.length > 0) {
                      //     files.forEach((file: any) => {
                      //       if (file.S3FilePath) {
                      //         window.open(
                      //           `${file.S3FilePath}#view=FitH&zoom=page-width`,
                      //           '_blank'
                      //         );
                      //       }
                      //     });
                      //   }
                      // }}
                      onDoubleClick={() =>
                        openFilesInNewWindow(tabItem.data?.FileDetail || [])
                      }
                    >
                      <button className="nav-link d-flex align-items-center gap-1">
                        <span>{tabItem.Heading}</span>
                        {hasTabWarning && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              // <Tooltip
                              //   id={`operator-tab-warning-${originalIndex}`}
                              // >
                              //   {formatMessage('please_add_check_item')}
                              // </Tooltip>
                              <></>
                            }
                          >
                            <ExclamationCircleFill className="text-danger" />
                          </OverlayTrigger>
                        )}
                      </button>
                    </li>
                  </OverlayTrigger>
                );
              })}
            </ul>
            {isOverflowing && (
              <div
                ref={dropdownRef}
                className="position-absolute end-0 top-0 d-flex align-items-center z-3"
              >
                <button
                  className="btn py-2 btn-secondary"
                  onClick={() => setShowTabDropdown((prev) => !prev)}
                  title="All tabs"
                >
                  <ThreeDots />
                </button>
                {showTabDropdown && (
                  <div
                    className="dropdown-menu show"
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '100%',
                      minWidth: '180px',
                      maxHeight: '260px',
                      overflowY: 'auto',
                    }}
                  >
                    {sortedTabs.map(({ item: tabItem, originalIndex }) => (
                      <button
                        key={originalIndex}
                        className={`dropdown-item${activeIndex === originalIndex ? ' active' : ''}`}
                        onClick={() => handleDropdownTabClick(originalIndex)}
                      >
                        {tabItem.Heading}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="Multiple-images border rounded">
              {(mergedTabs[activeIndex]?.data?.IsDimension === true ||
                String(
                  mergedTabs[activeIndex]?.data?.IsDimension
                ).toLowerCase() === 'true' ||
                mergedTabs[activeIndex]?.data?.isDimension === true ||
                String(
                  mergedTabs[activeIndex]?.data?.isDimension
                ).toLowerCase() === 'true') &&
                dims &&
                dims.length > 0 && (
                  <div className="tab_block border rounded">
                    <div className="tab_operator_inner">
                      <div className="tableDesign operator-dimension-table">
                        <div className="table-responsive">
                          <div className="react-bootstrap-table">
                            <table className="table table-hover table-bordered">
                              <thead>
                                <tr>
                                  {dims.map((dim: any) => (
                                    <th key={dim.DimensionColumn}>
                                      {dim.DimensionColumn}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {dimensionTable.map((row, rowIndex) => (
                                  <tr key={rowIndex}>
                                    {dims.map((dim: any) => {
                                      const rawValue = row[
                                        dim.DimensionColumn
                                      ] || {
                                        value: '',
                                        isDefaultChecked: false,
                                      };
                                      const { value, isDefaultChecked } =
                                        rawValue;
                                      const key = `${activeIndex}-${rowIndex}-${dim.Id}`;
                                      const legacyKey = `${rowIndex}-${dim.Id}`;
                                      const stringValue =
                                        value === null || value === undefined
                                          ? ''
                                          : String(value).trim();
                                      const hasValue =
                                        stringValue !== '' &&
                                        stringValue !== '-' &&
                                        stringValue !== '--';
                                      return (
                                        <td key={key}>
                                          {dim.DimensionType === 1 &&
                                          hasValue ? (
                                            <span className="d-flex align-items-center">
                                              <Form.Check
                                                type="checkbox"
                                                id={`dim-${key}`}
                                                checked={
                                                  dimensionChecks[key] ??
                                                  dimensionChecks[legacyKey] ??
                                                  isDefaultChecked
                                                }
                                                onChange={(e) =>
                                                  handleDimensionCheck(
                                                    activeIndex,
                                                    rowIndex,
                                                    dim.Id,
                                                    e.target.checked
                                                  )
                                                }
                                                disabled={!isTimerRunning}
                                              />
                                              <span className="ps-2">
                                                {value
                                                  .split('\n')
                                                  .map((line, index) => {
                                                    const displayLine =
                                                      line.trim() === ''
                                                        ? '\u00A0'
                                                        : line;
                                                    return (
                                                      <span
                                                        key={index}
                                                        className="d-block"
                                                      >
                                                        {displayLine}
                                                      </span>
                                                    );
                                                  })}
                                              </span>
                                            </span>
                                          ) : (
                                            <>
                                              {value
                                                .split('\n')
                                                .map((line, index) => {
                                                  const displayLine =
                                                    line.trim() === ''
                                                      ? '\u00A0'
                                                      : line;
                                                  return (
                                                    <span
                                                      key={index}
                                                      className="p-1 d-block"
                                                    >
                                                      {displayLine}
                                                    </span>
                                                  );
                                                })}
                                            </>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              {mergedTabs[activeIndex]?.type === 'image' &&
                (allFilesAreNull ? (
                  <div className="message no-teplar-data">
                    <div className="d-flex message-area">
                      <div className="no-data-img text-center">
                        <img
                          src={img404}
                          alt={formatMessage('picture_label')}
                        />
                        <p>
                          {' '}
                          <FormattedMessage id="no_holiday_message" />{' '}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    {validFiles?.map((file: any, idx: number) => (
                      <div
                        key={idx}
                        className="images-operator-tabs text-center"
                      >
                        {file.FileType === 'application/pdf' ? (
                          <iframe
                            src={`${file.S3FilePath}#view=FitH&zoom=page-width`}
                            style={{ border: 'none', width: '100%' }}
                          />
                        ) : (
                          // <CanvasDrawOnImage
                          //   imageUrl={file.S3FilePath}
                          //   initialData={
                          //     canvasDrawings[`${activeIndex}-${idx}`]
                          //   }
                          //   onSave={(dataUrl) =>
                          //     handleCanvasChange(
                          //       `${activeIndex}-${idx}`,
                          //       dataUrl
                          //     )
                          //   }
                          // />
                          <figure className="m-0">
                            <img
                              src={file.S3FilePath}
                              alt={file.Heading}
                            />
                          </figure>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div
          className={`${Number(processType) === 2 ? 'col-4' : `${Number(processType) === 3 ? 'col-8 col-xxl-8 picking-horizontal-right' : 'col-4 print-horizontal'}`}`}
        >
          <div className="horizontal-right-section">
            {/* Print Table (if processType === 2) */}
            {Number(processType) === 2 && (
              <div className="horizontal-right-section-top">
                <TeplarPrintTable
                  operatorTeplarDetails={operatorTeplarDetails}
                  isTimerRunning={isTimerRunning}
                  isProcessStarted={isProcessStarted}
                  printCount={printCount}
                  handleDecrementPrintCount={handleDecrementPrintCount}
                  handleIncrementPrintCount={handleIncrementPrintCount}
                  handleChangePrintCount={handleChangePrintCount}
                  stringToHex={stringToHex}
                  onSaveTeplarOrder={onSaveTeplarOrder}
                  onOrderChange={onTeplarOrderChange}
                  teplarList={teplarList}
                  setTeplarList={setTeplarList}
                  saveClicked={saveClicked}
                  setSaveClicked={setSaveClicked}
                />
              </div>
            )}

            {/* Print BOM Table (if processType === 3) */}
            {Number(processType) === 3 && (
              <div className="horizontal-right-section-top">
                <BOMTable
                  bomDataList={bomDataList}
                  isTimerRunning={isTimerRunning}
                  setBomDataListState={setBomDataListState}
                  bomDataListState={bomDataListState}
                />
              </div>
            )}
            <div className="box2 horizontal-right-section-middle">
              <p className="small text-muted">{formatMessage('check_items')}</p>
              <CheckItemsData
                checkItems={checkItems}
                isTimerRunning={isTimerRunning}
                activeIndex={activeIndex}
                setSubmittedCheckItems={setSubmittedCheckItems}
                submittedCheckItems={submittedCheckItems}
              />
            </div>
            {/* Box 2: Check Items and Memo */}
            <div className="memo-message horizontal-right-section-bottom">
              <p className="small text-muted">
                {formatMessage('memo_message')}
              </p>
              <textarea
                className="form-control"
                maxLength={256}
                placeholder={formatMessage('Enter Message')}
                value={
                  memo[
                    mergedTabs[activeIndex]?.data?.TabIndex ?? activeIndex
                  ] || ''
                }
                onChange={(e) => {
                  const memoKey =
                    mergedTabs[activeIndex]?.data?.TabIndex ?? activeIndex;
                  handleMemoChange(memoKey, e.target.value);
                }}
                disabled={!isTimerRunning}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatorHorizontalView;
